/* eslint-disable react/prop-types */
import { FC, useEffect, useId, useState } from "react";
import { X, Search, Check, Users, Smartphone, Tags, User } from "lucide-react";
import cmsClient from "src/services/cmsClient";
import useAdminGroups from "src/features/Groups/hooks/useAdminGroups";

interface AudienceUser {
	cmsUserId: string;
	firstName: string;
	lastName: string;
	department: string;
}

type AudienceType = "everyone" | "platform" | "groups" | "users";
type AudiencePlatform = "ios" | "android" | "both";

interface Props {
	audienceType: AudienceType;
	audiencePlatform: AudiencePlatform;
	audienceUserIds: string[];
	audienceGroupIds: number[];
	onAudienceTypeChange: (type: AudienceType) => void;
	onPlatformChange: (platform: AudiencePlatform) => void;
	onUserIdsChange: (ids: string[]) => void;
	onGroupIdsChange: (ids: number[]) => void;
}

const TABS: {
	value: AudienceType;
	label: string;
	description: string;
	icon: typeof Users;
}[] = [
	{
		value: "everyone",
		label: "Everyone",
		description: "All users",
		icon: Users
	},
	{
		value: "groups",
		label: "Groups",
		description: "Members of …",
		icon: Tags
	},
	{
		value: "users",
		label: "Users",
		description: "Specific people",
		icon: User
	},
	{
		value: "platform",
		label: "Platform",
		description: "iOS or Android",
		icon: Smartphone
	}
];

const PLATFORMS: { value: AudiencePlatform; label: string }[] = [
	{ value: "both", label: "Both" },
	{ value: "android", label: "Android" },
	{ value: "ios", label: "iOS" }
];

const AudiencePicker: FC<Props> = ({
	audienceType,
	audiencePlatform,
	audienceUserIds,
	audienceGroupIds,
	onAudienceTypeChange,
	onPlatformChange,
	onUserIdsChange,
	onGroupIdsChange
}) => {
	const tablistId = useId();
	const [userSearch, setUserSearch] = useState("");
	const [allUsers, setAllUsers] = useState<AudienceUser[]>([]);
	const [usersError, setUsersError] = useState(false);
	const [groupSearch, setGroupSearch] = useState("");

	const { data: allGroups = [], isError: groupsError } = useAdminGroups();

	useEffect(() => {
		if (audienceType !== "users") return;
		let cancelled = false;
		setUsersError(false);
		cmsClient
			.get<
				Array<{
					id: string;
					first_name: string;
					last_name: string;
					department: string;
				}>
			>("/announcements/users/")
			.then((res) => {
				if (cancelled) return;
				setAllUsers(
					res.data.map((u) => ({
						cmsUserId: u.id,
						firstName: u.first_name,
						lastName: u.last_name,
						department: u.department ?? ""
					}))
				);
			})
			.catch(() => {
				if (cancelled) return;
				setAllUsers([]);
				setUsersError(true);
			});
		return () => {
			cancelled = true;
		};
	}, [audienceType]);

	const filteredUsers = allUsers.filter(
		(user) =>
			!audienceUserIds.includes(user.cmsUserId) &&
			(`${user.firstName} ${user.lastName}`
				.toLowerCase()
				.includes(userSearch.toLowerCase()) ||
				user.department.toLowerCase().includes(userSearch.toLowerCase()))
	);

	const selectedUsers = allUsers.filter((user) =>
		audienceUserIds.includes(user.cmsUserId)
	);

	const addUser = (cmsUserId: string) => {
		onUserIdsChange([...audienceUserIds, cmsUserId]);
		setUserSearch("");
	};

	const removeUser = (cmsUserId: string) => {
		onUserIdsChange(audienceUserIds.filter((id) => id !== cmsUserId));
	};

	// Groups list comes from useAdminGroups (Group.id is a string|number
	// union for legacy compatibility, but MeeGroup ids are numeric long
	// from the backend — coerce to number on selection).
	const selectedGroups = allGroups.filter((g) =>
		audienceGroupIds.includes(Number(g.id))
	);
	const filteredGroups = allGroups.filter(
		(g) =>
			!audienceGroupIds.includes(Number(g.id)) &&
			g.name.toLowerCase().includes(groupSearch.toLowerCase())
	);

	const addGroup = (id: number) => {
		onGroupIdsChange([...audienceGroupIds, id]);
		setGroupSearch("");
	};

	const removeGroup = (id: number) => {
		onGroupIdsChange(audienceGroupIds.filter((existing) => existing !== id));
	};

	const platformLabel =
		PLATFORMS.find((p) => p.value === audiencePlatform)?.label ?? "iOS";
	const summary: Record<AudienceType, JSX.Element> = {
		everyone: (
			<>
				This announcement will be shown to <strong>everyone</strong>.
			</>
		),
		platform: (
			<>
				This announcement will be shown to{" "}
				<strong>
					{audiencePlatform === "both"
						? "iOS and Android users"
						: `${platformLabel} users only`}
				</strong>
				.
			</>
		),
		groups:
			selectedGroups.length === 0 ? (
				<>
					This announcement will be shown to <strong>no one yet</strong> — add
					at least one group.
				</>
			) : (
				<>
					This announcement will be shown to members of{" "}
					<strong>{selectedGroups.map((g) => g.name).join(", ")}</strong>.
				</>
			),
		users:
			selectedUsers.length === 0 ? (
				<>
					This announcement will be shown to <strong>no one yet</strong> — add
					at least one user.
				</>
			) : (
				<>
					This announcement will be shown to{" "}
					<strong>
						{selectedUsers
							.map((u) => `${u.firstName} ${u.lastName}`)
							.join(", ")}
					</strong>
					.
				</>
			)
	};

	return (
		<div className="flex flex-col gap-4">
			{/* Single-select option cards — clearly mutually exclusive */}
			<fieldset>
				<legend className="text-sm font-medium text-foreground mb-1">
					Show this announcement to
				</legend>
				<p className="text-xs text-muted-foreground mb-3">
					Choose one. Switching options replaces your current selection.
				</p>
				<div
					role="radiogroup"
					aria-label="Audience type"
					id={tablistId}
					onKeyDown={(e) => {
						// ARIA radiogroup roving focus per WAI-ARIA Authoring
						// Practices: Left/Up move to previous, Right/Down move to
						// next (both wrapping). Home jumps to first, End to last.
						// Activating a new option also focuses its button.
						let targetIndex: number | null = null;
						const currentIndex = TABS.findIndex(
							(t) => t.value === audienceType
						);
						if (e.key === "ArrowRight" || e.key === "ArrowDown") {
							targetIndex = (currentIndex + 1) % TABS.length;
						} else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
							targetIndex = (currentIndex - 1 + TABS.length) % TABS.length;
						} else if (e.key === "Home") {
							targetIndex = 0;
						} else if (e.key === "End") {
							targetIndex = TABS.length - 1;
						}
						if (targetIndex === null) return;
						e.preventDefault();
						const next = TABS[targetIndex];
						onAudienceTypeChange(next.value);
						const nextEl = document.getElementById(
							`${tablistId}-radio-${next.value}`
						) as HTMLButtonElement | null;
						nextEl?.focus();
					}}
					className="grid grid-cols-2 md:grid-cols-4 gap-2"
				>
					{TABS.map((tab) => {
						const active = audienceType === tab.value;
						const Icon = tab.icon;
						return (
							<button
								key={tab.value}
								id={`${tablistId}-radio-${tab.value}`}
								type="button"
								role="radio"
								aria-checked={active}
								tabIndex={active ? 0 : -1}
								onClick={() => onAudienceTypeChange(tab.value)}
								className={[
									"relative flex flex-col items-start gap-1 rounded-md border p-3 text-left transition-all focus:outline-none focus:ring-2 focus:ring-ring",
									active
										? "border-primary bg-primary-lighter shadow-sm"
										: "border-border bg-card hover:border-input"
								].join(" ")}
							>
								{active && (
									<Check
										className="absolute top-2 right-2 h-4 w-4 text-primary"
										aria-hidden="true"
									/>
								)}
								<Icon
									className={[
										"h-4 w-4",
										active ? "text-primary" : "text-muted-foreground"
									].join(" ")}
									aria-hidden="true"
								/>
								<span
									className={[
										"text-sm font-medium",
										active ? "text-foreground" : "text-foreground"
									].join(" ")}
								>
									{tab.label}
								</span>
								<span className="text-xs text-muted-foreground">
									{tab.description}
								</span>
							</button>
						);
					})}
				</div>
			</fieldset>

			<p
				className="text-sm text-muted-foreground"
				aria-live="polite"
				data-testid="audience-summary"
			>
				{summary[audienceType]}
			</p>

			{audienceType === "everyone" && (
				<div className="flex items-center justify-center rounded-md border border-dashed border-border bg-subtle py-6">
					<span className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 px-2.5 py-0.5 text-xs font-medium">
						Everyone — no restrictions
					</span>
				</div>
			)}

			{audienceType === "platform" && (
				<div className="flex rounded-md border border-border overflow-hidden self-start">
					{PLATFORMS.map((option, idx) => (
						<button
							key={option.value}
							type="button"
							aria-pressed={audiencePlatform === option.value}
							onClick={() => onPlatformChange(option.value)}
							className={[
								"px-4 py-2 text-sm font-medium transition-colors",
								idx > 0 ? "border-l border-border" : "",
								audiencePlatform === option.value
									? "bg-foreground text-background"
									: "bg-card text-muted-foreground hover:bg-muted"
							].join(" ")}
						>
							{option.label}
						</button>
					))}
				</div>
			)}

			{audienceType === "groups" && (
				<div className="flex flex-col gap-3">
					{groupsError && (
						<p role="alert" className="text-sm text-destructive">
							Failed to load groups. Please try again.
						</p>
					)}
					{selectedGroups.length > 0 && (
						<div className="flex flex-wrap gap-1.5">
							{selectedGroups.map((group) => (
								<span
									key={group.id}
									className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary text-xs font-medium px-2.5 py-0.5"
								>
									{group.name}
									<button
										type="button"
										aria-label={`Remove ${group.name}`}
										onClick={() => removeGroup(Number(group.id))}
										className="hover:opacity-70 transition-opacity"
									>
										<X className="h-3 w-3" />
									</button>
								</span>
							))}
						</div>
					)}
					<div className="relative">
						<Search
							size={16}
							aria-hidden="true"
							className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
						/>
						<input
							type="text"
							value={groupSearch}
							onChange={(e) => setGroupSearch(e.target.value)}
							placeholder="Search groups…"
							aria-label="Search groups"
							className="rounded-md border border-input bg-card pl-9 pr-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-ring"
						/>
					</div>
					{groupSearch.length > 0 && filteredGroups.length > 0 && (
						<div className="border border-border rounded-md bg-card shadow-sm max-h-[200px] overflow-y-auto">
							{filteredGroups.map((group) => (
								<button
									key={group.id}
									type="button"
									onClick={() => addGroup(Number(group.id))}
									className="flex items-center justify-between w-full px-3 py-2 text-sm hover:bg-muted text-left"
								>
									<span className="font-medium text-foreground">
										{group.name}
									</span>
								</button>
							))}
						</div>
					)}
					{groupSearch.length > 0 && filteredGroups.length === 0 && (
						<p className="text-sm text-muted-foreground">No groups found</p>
					)}
				</div>
			)}

			{audienceType === "users" && (
				<div className="flex flex-col gap-3">
					{usersError && (
						<p role="alert" className="text-sm text-destructive">
							Failed to load users. Please try again.
						</p>
					)}
					{selectedUsers.length > 0 && (
						<div className="flex flex-wrap gap-1.5">
							{selectedUsers.map((user) => (
								<span
									key={user.cmsUserId}
									className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary text-xs font-medium px-2.5 py-0.5"
								>
									{user.firstName} {user.lastName}
									<button
										type="button"
										aria-label={`Remove ${user.firstName} ${user.lastName}`}
										onClick={() => removeUser(user.cmsUserId)}
										className="hover:opacity-70 transition-opacity"
									>
										<X className="h-3 w-3" />
									</button>
								</span>
							))}
						</div>
					)}
					<div className="relative">
						<Search
							size={16}
							aria-hidden="true"
							className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
						/>
						<input
							type="text"
							value={userSearch}
							onChange={(e) => setUserSearch(e.target.value)}
							placeholder="Search users…"
							aria-label="Search users"
							className="rounded-md border border-input bg-card pl-9 pr-9 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-ring"
						/>
						{userSearch && (
							<button
								type="button"
								aria-label="Clear search"
								onClick={() => setUserSearch("")}
								className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
							>
								<X size={14} aria-hidden="true" />
							</button>
						)}
					</div>
					{userSearch.length > 0 && filteredUsers.length > 0 && (
						<div className="border border-border rounded-md bg-card shadow-sm max-h-[200px] overflow-y-auto">
							{filteredUsers.map((user) => (
								<button
									key={user.cmsUserId}
									type="button"
									onClick={() => addUser(user.cmsUserId)}
									className="flex items-center justify-between w-full px-3 py-2 text-sm hover:bg-muted text-left"
								>
									<span className="font-medium text-foreground">
										{user.firstName} {user.lastName}
									</span>
									<span className="text-muted-foreground text-xs">
										{user.department}
									</span>
								</button>
							))}
						</div>
					)}
					{userSearch.length > 0 && filteredUsers.length === 0 && (
						<p className="text-sm text-muted-foreground">No users found</p>
					)}
				</div>
			)}
		</div>
	);
};

export default AudiencePicker;
